import type { CartItemData } from "@/components/cart/types"
import type { SellerDetail } from "@/components/cart/paypal-checkout"
import { calculateSellerShippingTotal as sharedCalculateSellerShippingTotal } from "@/lib/payments/cart-math"

/**
 * Calculate shipping total for a set of items belonging to one seller.
 * Re-exported here as a thin wrapper for backwards compatibility with
 * existing import sites.
 */
export function calculateSellerShippingTotal(items: CartItemData[]): number {
  return sharedCalculateSellerShippingTotal(items)
}

/**
 * Group cart items by seller and build SellerDetail[] for PayPalCheckout.
 */
export function buildSellerDetails(items: CartItemData[]): SellerDetail[] {
  const bySeller = new Map<
    string,
    { sellerId: string; items: CartItemData[]; paypalMerchantId: string | null; paypalCapabilities: string[] }
  >()

  for (const item of items) {
    const key = item.sellerId && item.sellerId.trim().length > 0 ? item.sellerId : `unknown-${item.listingId}`
    const group = bySeller.get(key) ?? {
      sellerId: key,
      items: [] as CartItemData[],
      paypalMerchantId: (item as any).paypalMerchantId as string | null,
      paypalCapabilities: item.paypalCapabilities ?? ([] as string[]),
    }
    group.items.push(item)
    group.paypalMerchantId = group.paypalMerchantId || ((item as any).paypalMerchantId as string | null)
    if (!group.paypalCapabilities.length && item.paypalCapabilities?.length) {
      group.paypalCapabilities = item.paypalCapabilities
    }
    bySeller.set(key, group)
  }

  return Array.from(bySeller.values()).map((g) => ({
    sellerId: g.sellerId,
    paypalMerchantId: g.paypalMerchantId || "",
    capabilities: g.paypalCapabilities ?? [],
  }))
}
